import { useCallback, useEffect, useRef, useState } from 'react';
import useCalendar from '../useCalendar';
import { DEFAULT_MAX_MONTH_INDEX } from '../constants/dates';
import getMonthIndex from '../utils/getMonthIndex';
import getMonthsInView from '../utils/getMonthsInView';
const DEBOUNCE_TIMEOUT = 100;
let scrollTimeout;
function useScroll() {
  const {
    firstValidMonth,
    maxMonthIndex,
    monthIndex,
    monthsLength = DEFAULT_MAX_MONTH_INDEX,
    monthsToShow,
    monthWidth,
    startMonth,
    onDisplayMonthsChange,
    setMonthIndex,
    setTabClose
  } = useCalendar();
  const scrollBoxDOMNode = useRef();
  const scrollContainerDOMNode = useRef();
  const [disableLeftArrow, setDisableLeftArrow] = useState(monthIndex <= 0);
  const [disableRightArrow, setDisableRightArrow] = useState(monthIndex + monthsToShow >= monthsLength);
  const [shouldScroll, setShouldScroll] = useState(false);
  const dispatchMonthsInView = useCallback(() => {
    if (!scrollBoxDOMNode || !scrollBoxDOMNode.current) {
      return;
    }
    const calendarWrapper = scrollBoxDOMNode.current.children[0] || null;
    const monthsInView = getMonthsInView(startMonth, calendarWrapper, scrollContainerDOMNode.current);
    onDisplayMonthsChange(monthsInView);
    return monthsInView;
  }, [scrollBoxDOMNode, scrollContainerDOMNode]);
  const onArrowFocus = useCallback(() => {
    setTabClose(true);
  }, []);
  const scrollCalendar = useCallback(() => {
    setShouldScroll(false);
    if (scrollContainerDOMNode?.current) {
      scrollContainerDOMNode.current.scrollLeft = monthIndex * monthWidth;
    }
  }, [scrollContainerDOMNode, monthIndex, monthWidth]);
  const scrollLeft = useCallback(evt => {
    evt?.preventDefault();
    setMonthIndex(prev => prev - 1);
    setDisableLeftArrow(monthIndex <= 1);
    setDisableRightArrow(false);
    setShouldScroll(true);
  }, [monthIndex, dispatchMonthsInView]);
  const scrollRight = useCallback(evt => {
    evt?.preventDefault();
    setMonthIndex(prev => prev + 1);
    setDisableLeftArrow(false);
    setDisableRightArrow(monthIndex >= maxMonthIndex - monthsToShow + 1);
    setShouldScroll(true);
  }, [monthIndex, maxMonthIndex, dispatchMonthsInView]);
  const onScroll = useCallback(() => {
    if (scrollTimeout) {
      clearTimeout(scrollTimeout);
    }
    scrollTimeout = setTimeout(() => {
      const months = dispatchMonthsInView();
      const newMonthIndex = getMonthIndex(firstValidMonth, months);
      setMonthIndex(newMonthIndex);
      setDisableLeftArrow(newMonthIndex <= 0);
      setDisableRightArrow(newMonthIndex >= maxMonthIndex - monthsToShow + 2);
    }, DEBOUNCE_TIMEOUT);
  }, [firstValidMonth, dispatchMonthsInView]);
  useEffect(() => {
    if (shouldScroll) {
      scrollCalendar();
      dispatchMonthsInView();
    }
  }, [shouldScroll]);
  return {
    monthIndex,
    scrollBoxDOMNode,
    scrollContainerDOMNode,
    disableLeftArrow,
    disableRightArrow,
    onArrowFocus,
    onScroll,
    scrollLeft,
    scrollRight
  };
}
export default useScroll;